import {
  AddRounded,
  AutoStoriesRounded,
  ChecklistRounded,
  ContentCopyRounded,
  DeleteOutlineRounded,
  ExpandMoreRounded,
  FolderOutlined,
  MoreHorizRounded,
  NorthRounded,
  RecordVoiceOverOutlined,
  ShuffleRounded,
  SignalCellularAltRounded,
  StarOutlineRounded,
  TuneRounded,
} from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { Page, PageHeader } from "../../../components/layout/Page";
import ActionMenu from "../../../components/ui/ActionMenu";
import Button from "../../../components/ui/Button";
import Chip from "../../../components/ui/Chip";
import EmptyState from "../../../components/ui/EmptyState";
import IconButton from "../../../components/ui/IconButton";
import SearchField from "../../../components/ui/SearchField";
import TermListFilterSheet from "./TermListFilterSheet";
import styles from "../css/TermListScreen.module.css";
import { useTranslation } from "react-i18next";
import BasicMenu from "../../common/BasicMenu";
import { BasicMenuButton } from "../../common/BasicMenu";
import { SortType } from "../../settings/model/SortType";
import { QuestionType } from "../../settings/model/QuestionType";
import { PronunciationPosition } from "../../settings/model/PronunciationPosition";
import {
  getPronPositioFromLocalStorage,
  getQuestionTypeFromLocalStorage,
  getSelectedGroupsFromLocalStorage,
  getSelectedLevelFromLocalStroage,
  getSortTypeFromLocalStorage,
  setPronPositionToLocalStorage,
  setQuestionTypeToLocalStorage,
  setSelectedGroupsToLocalStroage,
  setSelectedLevelsToLocalStroage,
  setSortTypeToLocalStroage,
} from "../../settings/util/termListSettings";
import { makeSelectedGroupsString } from "../../groups/groups";
import { TermLevel } from "../custom_view/TermLevel";
import {
  duplicatedTermsSorted,
  getFilteredTermsWithOptions,
  getFilteredTermsWithQuery,
  keepLocalTermState,
  makeSelectedLevelsString,
  toggleTermLevel,
} from "../terms";
import TermListGrid from "../custom_view/TermListGrid";
import Term from "../Term";
import { motion, useAnimation } from "framer-motion";
import GroupSelectScreen from "../../groups/GroupSelectScreen";
import Group from "../../groups/Group";
import { useAppSelector } from "../../../hook";
import { selectTerms } from "../termSlice";
import { fetchSentence } from "../../sentence/service/sentenceAPI";
import { generateMoreSentence } from "../../sentence/service/moreSentence";
import SentenceHistory from "../../sentence/model/SentenceHistory";
import VoiceLanguageSelectScreen from "../../voice/view/VoiceLanguageSelectScreen";
import Language from "../../language/Language";
import { setUserCustomLanguage } from "../../sentence/manager/sentenceSettings";
import { fetchGroupLanguageCode } from "../../groups/groupAPI";
import { isBrowserJapanese } from "../../common/brows";
import VoiceSettingScreen from "../../voice/view/VoiceSettingScreen";
import { TermSentenceWithStringValue } from "../../sentence/model/Sentence";

interface TermListScreenProps {
  isLoading: boolean;
  handleOnClickTermMarkButton: (term: Term) => void;
  handleOnClickLevelButton: (term: Term) => void;
  handleOnClickTermEditButton: (term: Term) => void;
  handleOnClickTermDeleteButton: (term: Term) => void;
  handleOnClickMarkListButton: () => void;
  handleOnClickTermAddButton: () => void;
  handleOnClickShuffleButton: () => void;
  handleOnClickSelectAllButton: () => void;
  handleOnClickCancelSelectionAllButton: () => void;
  handleOnClickSelectedTermsGroupChangeButton: (terms: Term[]) => void;
  handleOnClickSelectedTermsLevelChangeButton: (
    e: React.MouseEvent<HTMLDivElement>,
    terms: Term[]
  ) => void;
  handleOnClickSelectedTermsDeleteButton: (terms: Term[]) => void;
  handleOnClickCopySentenceToMemoButton: (
    item: TermSentenceWithStringValue
  ) => void;
}

export default function TermListScreen(props: TermListScreenProps) {
  const { t } = useTranslation();
  const controls = useAnimation();
  const [filteredTerms, setFilteredTerms] = useState<Term[]>([]);
  const [sentenceHistory] = useState(() => new SentenceHistory());
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isVisibleScrollToTopButton, setIsVisibleScrollToTopButton] =
    useState(false);

  const [selectedTerm, setSelectedTerm] = useState<Term | null>(null);

  const rawTerms = useAppSelector(selectTerms);

  const [isFindDuplicatesTermListMode, setIsFindDuplicatesTermListMode] =
    useState(false);

  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedTermsCount, setSelectedTermsCount] = useState(0);

  const [showGroupSelectScreen, setShowGroupSelectScreen] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState<Group[]>([]);
  const [selectedLevels, setSelectedLevels] = useState<TermLevel[]>([]);
  const [sortType, setSortType] = useState(SortType.recent);
  const [questionType, setQuestionType] = useState(QuestionType.termFirst);
  const [pronPosition, setPronPosition] = useState(
    PronunciationPosition.onTerm
  );

  const [searchText, setSearchText] = useState<string>();

  const [selectedGroupsString, setSelectedGroupsString] = useState("");
  const [selectedLevelsString, setSelectedLevelsString] = useState("");
  const [sortTypeString, setSortTypeString] = useState("");

  const [isOpenFilterSheet, setIsOpenFilterSheet] = useState(false);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<HTMLElement | null>(
    null
  );
  const [isOpenTermCellMoreMenu, setIsOpenTermCellMoreMenu] =
    useState<Term | null>(null);
  const [
    isOpenSentenceLanguageSelectScreen,
    setIsOpenSentenceLanguageSelectScreen,
  ] = useState(false);
  const [isOpenVoiceSetting, setIsOpenVoiceSetting] = useState(false);
  const [showingSentenceMoreMenu, setShowingSentenceMoreMenu] =
    useState<TermSentenceWithStringValue>();

  const getTermCellMoreButtons = (): BasicMenuButton[] => [
    {
      title: t("Edit"),
      handleOnClose: () => {
        const term = isOpenTermCellMoreMenu;
        if (term !== null) {
          props.handleOnClickTermEditButton(term);
        }
        handleOnCloseTermCellMoreMenu();
      },
    },
    {
      title: t("Delete"),
      handleOnClose: () => {
        const term = isOpenTermCellMoreMenu;
        if (term !== null) {
          props.handleOnClickTermDeleteButton(term);
        }
        handleOnCloseTermCellMoreMenu();
      },
    },
  ];

  const getTermCellSentenceMoreButtons = (): BasicMenuButton[] => [
    {
      title: t("ExampleSaveToTermMemo"),
      handleOnClose: () => {
        const item = showingSentenceMoreMenu;
        if (item !== undefined) {
          props.handleOnClickCopySentenceToMemoButton(item);
        }
        setShowingSentenceMoreMenu(undefined);
      },
    },
  ];

  useEffect(() => {
    setSelectedGroups(getSelectedGroupsFromLocalStorage());
    setSelectedLevels(getSelectedLevelFromLocalStroage());
    setSelectedGroups(getSelectedGroupsFromLocalStorage());
    setSortType(getSortTypeFromLocalStorage());
    setQuestionType(getQuestionTypeFromLocalStorage());
    setPronPosition(getPronPositioFromLocalStorage());
  }, []);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;

    if (showGroupSelectScreen) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [showGroupSelectScreen]);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;

    if (isOpenSentenceLanguageSelectScreen) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpenSentenceLanguageSelectScreen]);

  useEffect(() => {
    if (searchText !== undefined && searchText.length !== 0) {
      setFilteredTerms(
        getFilteredTermsWithQuery(
          searchText,
          getFilteredTermsWithOptions(rawTerms)
        )
      );
    } else {
      setFilteredTerms(rawTerms);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  useEffect(() => {
    updateTermList();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawTerms]);

  useEffect(() => {
    updateTermList();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFindDuplicatesTermListMode]);

  useEffect(() => {
    updateSelectedGroupsString(selectedGroups);
    updateSelectedLevelsString(selectedLevels);
    updateSortTypeString(sortType);
    updateTermList();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortType, questionType, pronPosition, selectedGroups, selectedLevels]);

  useEffect(() => {
    updateSelectedTermsCount();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredTerms]);

  useEffect(() => {
    if (!isSelectionMode) {
      props.handleOnClickCancelSelectionAllButton();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelectionMode]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  function handleScroll() {
    if (window.scrollY > 300) {
      setIsVisibleScrollToTopButton(true);
    } else {
      setIsVisibleScrollToTopButton(false);
    }
  }

  function scrollToTop() {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function updateTermLevel(checked: boolean, level: TermLevel) {
    const newLevels = toggleTermLevel(
      checked,
      level,
      getSelectedLevelFromLocalStroage()
    );
    setSelectedLevelsToLocalStroage(newLevels);
    setSelectedLevels(newLevels);
  }

  function updateSelectedTermsCount() {
    const count = filteredTerms.filter((term) => term.selected).length;
    setSelectedTermsCount(count);
  }

  function updateTermList() {
    let newTerms: Term[] = [];
    if (isFindDuplicatesTermListMode) {
      newTerms = duplicatedTermsSorted(rawTerms);
    } else {
      newTerms = getFilteredTermsWithOptions(
        rawTerms,
        undefined,
        sortType,
        selectedGroups,
        selectedLevels
      );
    }

    if (searchText !== undefined && searchText.length !== 0) {
      newTerms = getFilteredTermsWithQuery(
        searchText,
        getFilteredTermsWithOptions(rawTerms)
      );
    }

    setFilteredTerms((prevTerms) => keepLocalTermState(newTerms, prevTerms));
  }

  function updateSelectedGroupsString(selectedGroups: Group[]) {
    setSelectedGroupsString(makeSelectedGroupsString(selectedGroups));
  }

  function updateSelectedLevelsString(selectedLevels: TermLevel[]) {
    setSelectedLevelsString(makeSelectedLevelsString(selectedLevels));
  }

  function updateSortTypeString(sortType: SortType) {
    switch (sortType) {
      case SortType.recent:
        setSortTypeString(t("Latest"));
        break;
      case SortType.old:
        setSortTypeString(t("Old"));
        break;
      case SortType.abc:
        setSortTypeString(t("Alphabet"));
        break;
    }
  }

  function handleOnClickTermCellMoreButton(
    event: React.MouseEvent<HTMLDivElement>,
    term: Term
  ) {
    setAnchorEl(event.currentTarget);
    setIsOpenTermCellMoreMenu(term);
  }

  function handleOnClickTermCellSentenceMoreMenu(
    event: React.MouseEvent<HTMLDivElement>,
    item: TermSentenceWithStringValue
  ) {
    setAnchorEl(event.currentTarget);
    setShowingSentenceMoreMenu(item);
  }

  function handleOnClickGenerateSentenceButton(term: Term) {
    if (term.sentence === undefined) {
      generateSentence(term);
    } else {
      deleteSentence(term);
    }
  }

  function handleOnClickSentenceLanguageButton(term: Term) {
    setSelectedTerm(term);
    setIsOpenSentenceLanguageSelectScreen(true);
  }

  function handleOnClickMoreSentenceButton(term: Term) {
    generateMoreSentence(term, setFilteredTerms, sentenceHistory);
  }

  function handleOnCloseTermCellMoreMenu() {
    setIsOpenTermCellMoreMenu(null);
  }

  function handleOnClickSelectModeButton() {
    if (isSelectionMode) {
      setIsFindDuplicatesTermListMode(false);
    }
    setIsSelectionMode(!isSelectionMode);
  }

  function handleOnClickFindDuplicatesButton() {
    setIsFindDuplicatesTermListMode(true);
  }

  function handleOnClickCancelFindDuplicatesButton() {
    setIsFindDuplicatesTermListMode(false);
  }

  function handleOnClickSelectAllButton() {
    updateSelectionTerms(true);
  }

  function handleOnClickCancelSelectionAllButton() {
    updateSelectionTerms(false);
  }

  function handleOnClickVoiceSettingsButton() {
    setIsOpenVoiceSetting(true);
  }

  function handleOnClickTerm(term: Term, isSelectionMode: boolean) {
    const termIndex = filteredTerms.findIndex((t) => t.id === term.id);
    const newTerms = [...filteredTerms];
    if (termIndex !== -1) {
      if (isSelectionMode) {
        newTerms[termIndex] = {
          ...newTerms[termIndex],
          selected: !newTerms[termIndex].selected,
        };
      } else {
        newTerms[termIndex] = {
          ...newTerms[termIndex],
          isShowDetail: !newTerms[termIndex].isShowDetail,
        };
      }
    }
    setFilteredTerms(newTerms);
  }

  async function generateSentence(term: Term) {
    setFilteredTerms((prevTerms) => {
      const index = prevTerms.findIndex((t) => t.id === term.id);
      if (index === -1) return prevTerms;

      const updated = [...prevTerms];
      updated[index] = {
        ...updated[index],
        isGeneratingSentence: true,
      };
      return updated;
    });

    try {
      const languageCode =
        term.group.length !== 0 ? await fetchGroupLanguageCode(term.group) : "";
      const sentence = await fetchSentence(
        term.term,
        languageCode,
        sentenceHistory
      );

      setFilteredTerms((prevTerms) => {
        const index = prevTerms.findIndex((t) => t.id === term.id);
        if (index === -1) return prevTerms;

        const updated = [...prevTerms];
        updated[index] = {
          ...updated[index],
          sentence,
          isGeneratingSentence: false,
        };
        return updated;
      });
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
    }
  }

  function deleteSentence(term: Term) {
    const termIndex = filteredTerms.findIndex((t) => t.id === term.id);

    if (termIndex !== -1) {
      const newTerms = [...filteredTerms];
      const { sentence, ...rest } = newTerms[termIndex];
      const newTerm = { ...rest };

      newTerms[termIndex] = newTerm;

      setFilteredTerms(newTerms);
    }
  }

  function updateSelectionTerms(selection: boolean) {
    const newTerms = filteredTerms.map((term) => ({
      ...term,
      selected: selection,
    }));
    setFilteredTerms(newTerms);
  }

  function getSelectedTerms(): Term[] {
    const selectedTerms = filteredTerms.filter(
      (term) => term.selected === true
    );
    return selectedTerms;
  }

  function updateSentenceLanguage(language: Language) {
    setUserCustomLanguage(language.code);

    if (selectedTerm != null) {
      generateSentence(selectedTerm);
    }
  }

  const selectedTerms = isSelectionMode ? getSelectedTerms() : [];
  // 목록을 좁히는 설정 개수 (레벨 일부만 선택, 등록순이 아닌 정렬)
  const activeFilterCount =
    (selectedLevels.length > 0 && selectedLevels.length < 3 ? 1 : 0) +
    (sortType !== SortType.recent ? 1 : 0);

  return (
    <>
      <div className={styles.stack}>
        <motion.div animate={controls} className={styles.screen}>
          <Page width="medium">
            <PageHeader
              title={
                <button
                  type="button"
                  className={styles.groupButton}
                  onClick={() => setShowGroupSelectScreen(true)}
                >
                  <span className={styles.groupButtonText}>
                    {selectedGroupsString}
                  </span>
                  <ExpandMoreRounded className={styles.groupButtonIcon} />
                </button>
              }
              actions={
                <>
                  <Button
                    className={styles.desktopOnly}
                    leadingIcon={<AddRounded />}
                    onClick={() => props.handleOnClickTermAddButton()}
                  >
                    {t("TermListAddButton")}
                  </Button>
                  <IconButton
                    aria-label={t("MoreMenu")}
                    icon={<MoreHorizRounded />}
                    onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
                  />
                </>
              }
            />

            <SearchField
              className={styles.search}
              value={searchText ?? ""}
              onChange={(value) => setSearchText(value)}
              placeholder={t("SearchTermsNavTitle")}
              clearLabel={t("Cancel")}
            />

            <div className={styles.toolbar}>
              {isSelectionMode ? (
                <>
                  <Chip
                    onClick={() =>
                      selectedTermsCount === 0
                        ? handleOnClickSelectAllButton()
                        : handleOnClickCancelSelectionAllButton()
                    }
                  >
                    {selectedTermsCount === 0
                      ? t("SelectAllTerms")
                      : t("SelectCancelAllTerms", {
                          termsCount: selectedTermsCount,
                        })}
                  </Chip>
                  <Chip
                    disabled={selectedTermsCount === 0}
                    leadingIcon={<FolderOutlined />}
                    onClick={() =>
                      props.handleOnClickSelectedTermsGroupChangeButton(
                        selectedTerms
                      )
                    }
                  >
                    {t("EditGroupButton")}
                  </Chip>
                  <Chip
                    disabled={selectedTermsCount === 0}
                    leadingIcon={<SignalCellularAltRounded />}
                    onClick={(e) =>
                      props.handleOnClickSelectedTermsLevelChangeButton(
                        e as unknown as React.MouseEvent<HTMLDivElement>,
                        selectedTerms
                      )
                    }
                  >
                    {t("EditLevelButton")}
                  </Chip>
                  <Chip
                    disabled={selectedTermsCount === 0}
                    className={styles.dangerChip}
                    leadingIcon={<DeleteOutlineRounded />}
                    onClick={() =>
                      props.handleOnClickSelectedTermsDeleteButton(
                        selectedTerms
                      )
                    }
                  >
                    {t("Delete")}
                  </Chip>
                  <Chip
                    selected
                    onClick={() => handleOnClickSelectModeButton()}
                  >
                    {t("SelectionModeOffButton")}
                  </Chip>
                </>
              ) : isFindDuplicatesTermListMode ? (
                <>
                  <Chip selected leadingIcon={<ContentCopyRounded />}>
                    {t("FindDuplicatedTermListTitle")}
                  </Chip>
                  <Chip onClick={() => handleOnClickSelectModeButton()}>
                    {t("SelectionModeOnButton")}
                  </Chip>
                  <Chip onClick={handleOnClickCancelFindDuplicatesButton}>
                    {t("Cancel")}
                  </Chip>
                </>
              ) : (
                <>
                  <Chip
                    selected={activeFilterCount > 0}
                    leadingIcon={<TuneRounded />}
                    onClick={() => setIsOpenFilterSheet(true)}
                  >
                    {activeFilterCount > 0
                      ? `${t("Filter")} ${activeFilterCount}`
                      : t("Filter")}
                  </Chip>
                  <Chip
                    leadingIcon={<ShuffleRounded />}
                    onClick={() => {
                      setSortType(SortType.recent);
                      props.handleOnClickShuffleButton();
                    }}
                  >
                    {t("TermsShuffleButton")}
                  </Chip>
                  <Chip
                    leadingIcon={<StarOutlineRounded />}
                    onClick={() => props.handleOnClickMarkListButton()}
                  >
                    {t("ShowMarkedTermMenuTitle")}
                  </Chip>
                  <Chip
                    leadingIcon={<ChecklistRounded />}
                    onClick={() => handleOnClickSelectModeButton()}
                  >
                    {t("SelectionModeOnButton")}
                  </Chip>
                </>
              )}
            </div>

            <div className={styles.meta}>
              <span>
                {t("GroupSelectTermCount", {
                  termCount: filteredTerms.length,
                })}
              </span>
              {!isFindDuplicatesTermListMode && (
                <span className={styles.metaFilters}>
                  {selectedLevelsString} · {sortTypeString}
                </span>
              )}
            </div>

            <div className={styles.termsContainer}>
              <TermListGrid
                terms={filteredTerms}
                handleOnClick={handleOnClickTerm}
                handleOnClickMarkButton={props.handleOnClickTermMarkButton}
                handleOnClickLevelButton={props.handleOnClickLevelButton}
                handleOnClickMoreButton={(event, term) => {
                  handleOnClickTermCellMoreButton(event, term);
                }}
                handleOnClickGenerateSentenceButton={
                  handleOnClickGenerateSentenceButton
                }
                handleOnClickSentenceLanguageButton={
                  handleOnClickSentenceLanguageButton
                }
                handleOnClickMoreSentenceButton={
                  handleOnClickMoreSentenceButton
                }
                handleOnClickSentenceMoreButton={
                  handleOnClickTermCellSentenceMoreMenu
                }
                config={{
                  style: "list",
                  questionType: questionType,
                  pronunciationPosition: pronPosition,
                  isSelectionMode: isSelectionMode,
                }}
              />
              {rawTerms.length === 0 && props.isLoading === false && (
                <EmptyState
                  icon={<AutoStoriesRounded />}
                  description={t("TermListEmptyLabel")}
                  action={
                    <Button
                      leadingIcon={<AddRounded />}
                      onClick={() => props.handleOnClickTermAddButton()}
                    >
                      {t("TermListAddButton")}
                    </Button>
                  }
                />
              )}
            </div>
          </Page>

          <div className={styles.floatingButtons}>
            {isVisibleScrollToTopButton && (
              <IconButton
                aria-label={t("ScrollToTop")}
                icon={<NorthRounded />}
                variant="filled"
                onClick={scrollToTop}
              />
            )}
            <IconButton
              className={styles.mobileOnly}
              aria-label={t("TermListAddButton")}
              icon={<AddRounded />}
              variant="primary"
              size="lg"
              onClick={() => props.handleOnClickTermAddButton()}
            />
          </div>
        </motion.div>
        <GroupSelectScreen
          open={showGroupSelectScreen === true}
          selectStyle="multiple"
          showAllGroup={true}
          showNoGroup={true}
          showGroupAddButton={true}
          selectedGroups={selectedGroups.map((group: Group) => group.title)}
          handleOnSelectGroups={(groups: Group[]) => {
            setSelectedGroupsToLocalStroage(groups);
            setSelectedGroups(getSelectedGroupsFromLocalStorage());
          }}
          handleOnClose={() => {
            setShowGroupSelectScreen(false);
          }}
        />

        <VoiceSettingScreen
          open={isOpenVoiceSetting}
          handleOnClose={() => {
            setIsOpenVoiceSetting(false);
          }}
        />

        <TermListFilterSheet
          open={isOpenFilterSheet}
          onClose={() => setIsOpenFilterSheet(false)}
          resultCount={filteredTerms.length}
          selectedLevels={selectedLevels}
          onToggleLevel={updateTermLevel}
          sortType={sortType}
          onChangeSortType={(value) => {
            setSortTypeToLocalStroage(value);
            setSortType(value);
          }}
          questionType={questionType}
          onChangeQuestionType={(value) => {
            setQuestionTypeToLocalStorage(value);
            setQuestionType(value);
          }}
          pronPosition={pronPosition}
          onChangePronPosition={(value) => {
            setPronPositionToLocalStorage(value);
            setPronPosition(value);
          }}
          showChineseCharacterMode={!isBrowserJapanese()}
        />
        <ActionMenu
          open={moreMenuAnchor !== null}
          anchorEl={moreMenuAnchor}
          onClose={() => setMoreMenuAnchor(null)}
          items={[
            {
              label: t("voicesettings"),
              icon: <RecordVoiceOverOutlined />,
              onSelect: handleOnClickVoiceSettingsButton,
            },
            {
              label: t("FindDuplicatedTermListTitle"),
              icon: <ContentCopyRounded />,
              onSelect: handleOnClickFindDuplicatesButton,
            },
          ]}
        />
        <BasicMenu
          isOpen={isOpenTermCellMoreMenu !== null}
          anchorEl={anchorEl}
          buttons={getTermCellMoreButtons()}
          handleOnClose={() => handleOnCloseTermCellMoreMenu()}
        />
        <BasicMenu
          isOpen={showingSentenceMoreMenu !== undefined}
          anchorEl={anchorEl}
          buttons={getTermCellSentenceMoreButtons()}
          handleOnClose={() => setShowingSentenceMoreMenu(undefined)}
        />
        <VoiceLanguageSelectScreen
          open={isOpenSentenceLanguageSelectScreen === true}
          handleOnClickLanguage={updateSentenceLanguage}
          handleOnClose={() => {
            setIsOpenSentenceLanguageSelectScreen(false);
          }}
        />
      </div>
    </>
  );
}
